import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';

import {
  getRegionById,
  updateRegion,
  getAdminsInRegion,
} from '../../services/regions.services';
import { RegionStatus } from '../../types/fair.types';
import { usersService } from '../../services/users.service';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { States } from '../../types/auth.types';

//Interfaces for region and admin user
interface Region {
  id: string;
  name: string;
  location: string;
  regionDirectorId: string;
  state: string;
  status: RegionStatus;
  maxAmountOfFairs: number;
  schools: string[];
}

interface AdminUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  userType: string;
  regionId: string;
}

const RegionDetails: React.FC = () => {
  // Region Detail Hooks
  const [region, setRegion] = useState<Region | null>(null);
  const [regionAdmins, setRegionAdmins] = useState<AdminUser[]>([]);
  const [regionName, setRegionName] = useState('');
  const [regionLocation, setRegionLocation] = useState('');
  const [regionState, setRegionState] = useState('');
  const [regionMaxFairs, setRegionMaxFairs] = useState<string | number>('');
  const [regionStatus, setRegionStatus] = useState<RegionStatus>('pending');
  const [schools, setSchools] = useState<string[]>([]);
  const [newSchoolName, setNewSchoolName] = useState('');
  const [selectedDirectorId, setSelectedDirectorId] = useState('');
  // Dialog Hooks and States
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [schoolToDelete, setSchoolToDelete] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [directorInfo, setDirectorInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  //Auth and User Hooks
  const { authStatus } = useAuth();

  const fetchUserRegion = useCallback(async () => {
    setLoading(true);
    try {
      const userId = authStatus.user?.uid;

      if (!userId) {
        setError('User information not available.');
        setLoading(false);
        return;
      }

      const userRegionId = await usersService.getUserRegion(userId);
      const userRegion = await getRegionById(userRegionId);

      if (!userRegion) {
        setError('You are not associated with any region.');
        setLoading(false);
        return;
      }

      setRegion({
        id: userRegion.id,
        name: userRegion.name,
        location: userRegion.location,
        regionDirectorId: userRegion.regionDirectorId,
        state: userRegion.state,
        status: userRegion.status,
        maxAmountOfFairs: userRegion.maxAmountOfFairs,
        schools: userRegion.schools || [],
      });

      setRegionName(userRegion.name);
      setRegionLocation(userRegion.location);
      setRegionState(userRegion.state);
      setRegionStatus(userRegion.status);
      setRegionMaxFairs(userRegion.maxAmountOfFairs);
      setSchools(userRegion.schools || []);
      setSelectedDirectorId(userRegion.regionDirectorId);
    } catch (error) {
      console.error('Error fetching regions:', error);
      setError('Failed to load region information');
    } finally {
      setLoading(false);
    }
  }, [authStatus.user?.uid]);

  useEffect(() => {
    fetchUserRegion();
  }, [fetchUserRegion]);

  const fetchAdminsInRegion = useCallback(async () => {
    if (!region) return;
    try {
      const admins = await getAdminsInRegion(region.id);
      setRegionAdmins(admins || []);
    } catch (err) {
      console.error('Error fetching region admins:', err);
      setError('Failed to load region administrators');
    }
  }, [region]);

  const fetchDirectorInfo = useCallback(async () => {
    if (!region || !region.regionDirectorId) return;
    try {
      const userData = await usersService.getUserAccount(
        region.regionDirectorId
      );
      setDirectorInfo(userData);
    } catch (error) {
      console.error('Error fetching director details:', error);
    }
  }, [region]);

  useEffect(() => {
    if (region) {
      fetchAdminsInRegion();
      fetchDirectorInfo();
    }
  }, [region, fetchAdminsInRegion, fetchDirectorInfo]);

  //Get the status chip color based on the status of the region
  const getStatusChipColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  //Opening of the edit dialog and set the values for the region fields
  const handleOpenEditDialog = () => {
    if (region) {
      setRegionName(region.name);
      setRegionLocation(region.location);
      setRegionState(region.state);
      setRegionStatus(region.status as RegionStatus);
      setRegionMaxFairs(region.maxAmountOfFairs);
      setSelectedDirectorId(region.regionDirectorId);
    }
    //Open the edit dialog
    setEditDialogOpen(true);
  };

  //If user makes changes and saves, update it in the database
  const handleEditRegion = async () => {
    //Check if region is available to update
    if (!region) return;

    //Set an object with the updated data
    const updatedData = {
      name: regionName,
      location: regionLocation,
      state: regionState,
      status: regionStatus,
      maxAmountOfFairs: Number(regionMaxFairs),
      regionDirectorId: selectedDirectorId,
    };

    try {
      //Update the region with the new data
      await updateRegion(region.id, updatedData);

      //Refresh region data and close dialog
      fetchUserRegion();
      setEditDialogOpen(false);
    } catch (err) {
      console.error('Error updating region:', err);
      setError('Failed to update region');
    }
  };

  //Add a new school to the region
  const handleAddSchool = async () => {
    //Check if region and new school name are available (school not already in the list)
    if (!region || !newSchoolName.trim()) return;

    try {
      //Add the new school to the region's list of schools
      const updatedSchools = [...schools, newSchoolName.trim()];
      //Update the region with the new list of schools
      await updateRegion(region.id, { schools: updatedSchools });
      //Update the hook with the new list of schools
      setSchools(updatedSchools);
      //Reset the new school name input field
      setNewSchoolName('');
      console.log('School added successfully');
    } catch (err) {
      console.error('Error adding school:', err);
      setError('Failed to add school');
    }
  };

  //Open the delete dialog and set the school to be deleted
  const handleOpenDeleteDialog = (schoolToRemove: string) => {
    setSchoolToDelete(schoolToRemove);
    setDeleteDialogOpen(true);
  };

  //Remove a school from the region
  const handleRemoveSchool = async () => {
    //Check if region exists
    if (!region) return;

    try {
      //Remove the school from the region's list of schools
      const updatedSchools = schools.filter(
        (school) => school !== schoolToDelete
      );
      //Update the region with the new list of schools
      await updateRegion(region.id, { schools: updatedSchools });
      //Update the hook with the new list of schools
      setSchools(updatedSchools);
      //Close the school dialog
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error('Error removing school:', err);
      setError('Failed to remove school');
    }
  };

  //Screen loading and error handling
  if (loading) {
    return <Typography>Loading region information...</Typography>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!region) {
    return <Alert severity="info">No region information found.</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Region Details
      </Typography>

      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" gutterBottom>
                {region.name}
              </Typography>
              <Typography color="textSecondary" gutterBottom>
                Location: {region.location}, {region.state}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Maximum Fairs Allowed: {region.maxAmountOfFairs}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={region.status}
                  color={getStatusChipColor(region.status)}
                  sx={{ mr: 1 }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="h6" gutterBottom>
                Region Director:
              </Typography>
              {directorInfo ? (
                <>
                  <Typography>
                    {directorInfo.firstName} {directorInfo.lastName}
                  </Typography>
                  <Typography color="textSecondary">
                    {directorInfo.email}
                  </Typography>
                </>
              ) : (
                <Typography color="textSecondary">
                  {region.regionDirectorId || 'No director assigned'}
                </Typography>
              )}
            </Grid>
          </Grid>
        </CardContent>
        <CardActions>
          <Button
            startIcon={<Pencil size={16} />}
            onClick={handleOpenEditDialog}
            variant="contained"
            size="small"
          >
            Edit Region
          </Button>
        </CardActions>
      </Card>

      {/* Manage Schools Section */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Manage Schools
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TextField
              label="School Name"
              variant="outlined"
              size="small"
              value={newSchoolName}
              onChange={(e) => setNewSchoolName(e.target.value)}
              sx={{ mr: 2, flexGrow: 1 }}
            />
            <Button
              startIcon={<Plus size={16} />}
              variant="contained"
              size="medium"
              onClick={handleAddSchool}
              disabled={!newSchoolName.trim()}
            >
              Add School
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>School Name</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schools.length > 0 ? (
                  schools.map((school, index) => (
                    <TableRow key={index}>
                      <TableCell>{school}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(school)}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      No schools added yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Edit Region Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Region</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Region Name"
            fullWidth
            variant="outlined"
            margin="dense"
            value={regionName}
            onChange={(e) => setRegionName(e.target.value)}
          />
          <TextField
            label="Location"
            fullWidth
            variant="outlined"
            margin="dense"
            value={regionLocation}
            onChange={(e) => setRegionLocation(e.target.value)}
          />
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel id="state-select-label">State</InputLabel>
            <Select
              labelId="state-select-label"
              value={regionState}
              onChange={(e) => setRegionState(e.target.value)}
              label="State"
            >
              {Array.isArray(States) &&
                States.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel id="director-select-label">Region Director</InputLabel>
            <Select
              labelId="director-select-label"
              value={selectedDirectorId}
              onChange={(e) => setSelectedDirectorId(e.target.value)}
              label="Region Director"
            >
              {regionAdmins.map((admin) => (
                <MenuItem key={admin.id} value={admin.id}>
                  {admin.firstName} {admin.lastName} ({admin.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditRegion}
            variant="contained"
            color="primary"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this school?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRemoveSchool}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegionDetails;
